import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from "rxjs";
import { inject } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TWO_FACTOR_HTTP } from "../../api/enums/http-error-codes.enum";
import { SKIP_ERROR_SNACKBAR } from "./error.context";
import { CASE_ERROR_CODE, caseActionDisabledText, TRANSACTION_LOOKUP_ERROR_CODES } from "../../api/models/case.models";
import { SILENT_ERRORS } from "./silent-errors.context";

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const matSnackBar = inject(MatSnackBar);
    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            // Best-effort lookups opt out entirely — the caller degrades gracefully on its own.
            if (req.context.get(SILENT_ERRORS)) return throwError(() => error);
            // 2FA challenge (428) is owned by twoFactorInterceptor; `twoFactorHandled` marks
            // errors it already surfaced. Either way, don't pop a generic snackbar for them.
            const twoFactorHandled = (error as unknown as { twoFactorHandled?: boolean }).twoFactorHandled;
            const skipped = req.context.get(SKIP_ERROR_SNACKBAR);
            // why: 76/77/78 (stolen transaction not on chain / failed on chain / cannot check now, incl. the
            // 503) come only from the case stolen-transaction routes, and their dialogs show the backend
            // message inline. A generic toast on top would only repeat it as "Http failure response…".
            const inlineLookupError = TRANSACTION_LOOKUP_ERROR_CODES.includes(error.error?.code);
            if (error.error?.code !== 6 && error.status !== TWO_FACTOR_HTTP.REQUIRED && !twoFactorHandled && !skipped && !inlineLookupError) {
                // Every admin-survey call handles its own failures with a written-out message (and the
                // delete 409 opens an "archive instead" dialog), so the raw "Http failure response…"
                // toast is pure noise on top. (AML-874)
                // The external-case import routes are the same story: the create-case dialog renders
                // every failure inline (availability, search, preview, confirm), so a snackbar on top
                // would report one failure twice.
                // The case-management "Signals" and "Dashboard" tabs render their load failures inline
                // with a retry, for the same reason.
                const excluded: string[] = ['api/admin/surveys', 'api/admin/cases/import', 'api/history/MARKUP/',
                    'api/admin/cases/signals', 'api/admin/cases/dashboard'];
                // why: the "Markup details" dialog renders matrix load failures inline and maps the
                // dispatch 409 to "Nothing to send", which is not an error for the analyst.
                const markupMatrix = /api\/admin\/cases\/\d+\/markup(\?|$)/.test(error.url ?? '');
                if (!markupMatrix && excluded.map(excluded => error.url?.includes(excluded)).every((v) => v == false)) {
                    // why: 87 (action not allowed in Pre-investigate) needs the reason and the flag to turn on; the generic text would hide both.
                    // Its text is plain words, so it drops the default `break-all` panel class (kept for hashes) that splits words.
                    if (error.error?.code === CASE_ERROR_CODE.CASE_ACTION_DISABLED) {
                        matSnackBar.open(caseActionDisabledText(error.error?.message), 'Close', { panelClass: 'break-words' });
                    } else {
                        matSnackBar.open(error.message ?? 'Oops, something went wrong', 'Close');
                    }
                }
            }
            return throwError(() => error);
        })
    );
};
